import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getCantileverDocumentPageId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  shouldLookupCantileverPageHost,
  systemDocumentPageForPageNumber,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

import { useSystemDocumentByEntryId } from './useSystemDocumentByEntryId'

export interface KnownCantileverFileHosts {
  cantileverDocumentId?: string
  cantileverDocumentPageId?: string
  filename?: string
  systemDocumentEntryId?: string
  /** Bare BigQuery document_id for UUID5 host derive (list thumbs). */
  bqDocumentId?: string
  /**
   * True when document-level hosts were taken from an already-loaded
   * system-document entry (including when the cantilever host is absent).
   */
  hasHydratedDocumentHosts?: boolean
  /**
   * List thumbs: skip Rolodex page-host lookup (UUID5 derive + legacy fallback
   * handle resolution without waiting on /entries/load).
   */
  skipPageHostLookup?: boolean
}

/**
 * Resolve cantilever (+ system) file hosts for a system-document entry id + page.
 * Does not require an active pipeline-config (site-only tags for legacy HR).
 */
export function useCantileverFileHostsForDocument(
  systemDocumentEntryId: string | undefined,
  siteId: string,
  pageNumber = 1,
  knownHosts?: KnownCantileverFileHosts
): {
  systemDocumentEntryId: string | undefined
  cantileverDocumentId: string | undefined
  cantileverDocumentPageId: string | undefined
  filename: string | undefined
  bqDocumentId: string | undefined
  isLoading: boolean
} {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  // Skip Rolodex doc load when hosts are already known from hydration, or when
  // we already have the cantilever PDF host id.
  const skipDocumentLookup =
    !!knownHosts?.hasHydratedDocumentHosts || !!knownHosts?.cantileverDocumentId

  const entryIdForLookup =
    knownHosts?.systemDocumentEntryId ?? systemDocumentEntryId

  const {
    systemDocumentEntryId: resolvedSystemDocumentEntryId,
    cantileverDocumentId: resolvedCantileverDocumentId,
    filename: resolvedFilename,
    bqDocumentId: resolvedBqDocumentId,
    isLoading: isLoadingDoc,
  } = useSystemDocumentByEntryId(
    skipDocumentLookup ? undefined : entryIdForLookup,
    siteId
  )

  const resolvedEntryId =
    knownHosts?.systemDocumentEntryId ??
    resolvedSystemDocumentEntryId ??
    systemDocumentEntryId

  const cantileverDocumentId =
    knownHosts?.cantileverDocumentId ?? resolvedCantileverDocumentId
  const filename = knownHosts?.filename ?? resolvedFilename
  const bqDocumentId = knownHosts?.bqDocumentId ?? resolvedBqDocumentId

  // Legacy / pre-cantilever docs and list thumbs: skip page-host Rolodex.
  // Viewer still resolves tagged cantilever-document-page hosts when needed.
  const pageLoadOptions = useMemo(() => {
    if (
      knownHosts?.skipPageHostLookup ||
      isPipelineConfigScopeLoading ||
      !resolvedEntryId ||
      !shouldLookupCantileverPageHost(
        cantileverDocumentId,
        knownHosts?.cantileverDocumentPageId
      )
    ) {
      return undefined
    }
    return systemDocumentPageForPageNumber(
      resolvedEntryId,
      pageNumber,
      getPipelineConfigScopedTags({ site: siteId })
    )
  }, [
    cantileverDocumentId,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    knownHosts?.cantileverDocumentPageId,
    knownHosts?.skipPageHostLookup,
    pageNumber,
    resolvedEntryId,
    siteId,
  ])

  const { data: systemPages, isLoading: isLoadingPage } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentPageEntry[]>(
      pageLoadOptions
    )

  const cantileverDocumentPageId =
    knownHosts?.cantileverDocumentPageId ??
    (systemPages?.[0] ? getCantileverDocumentPageId(systemPages[0]) : undefined)

  return {
    systemDocumentEntryId: resolvedEntryId,
    cantileverDocumentId,
    cantileverDocumentPageId,
    filename,
    bqDocumentId,
    isLoading:
      (!skipDocumentLookup && isLoadingDoc) ||
      (!!pageLoadOptions && isLoadingPage),
  }
}
